// The dialog, with motion that exists.
//
// The generated component carries eight animation classes from `tw-animate-css`, a
// package this project does not install, plus a bare `duration-100`. Measured in the
// running app: `animationName: "none"` and `transitionProperty: "all"`. So a dialog
// appeared and vanished on the frame, and the only thing the duration touched was every
// property at once — including the focus ring, which then faded in behind the eye.
//
// # Why this composes the primitive rather than wrapping `DialogContent`
//
// The generated `DialogContent` renders its own `DialogOverlay` internally and offers
// no way to reach it. Wrapping it would fix the panel and leave the scrim on the dead
// classes — the page would dim instantly under a panel that faded in, which is worse
// than neither moving. So the content is assembled here from the same Base UI parts the
// generated file uses, and everything that is not animated is re-exported from it
// unchanged.
//
// This is the wrapper rule doing its job rather than an exception to it: `shadcn add`
// overwrites `src/components/ui/`, so the fix has to live somewhere it survives.

import { Dialog as DialogPrimitive } from "@base-ui/react/dialog";
import { XIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { overlayMotion, overlayScrim } from "./motion";

export {
  Dialog,
  DialogClose,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};

export type DialogContentProps = DialogPrimitive.Popup.Props & {
  /** The close button in the corner. Present unless the dialog supplies its own. */
  readonly showCloseButton?: boolean;
  /**
   * Suppresses the entrance and the exit.
   *
   * For a surface somebody opens dozens of times a day from the keyboard, where the
   * animation is latency paid on every invocation and skippable on none. The command
   * palette is the case this exists for.
   */
  readonly instant?: boolean;
};

export function DialogContent({
  className,
  children,
  showCloseButton = true,
  instant = false,
  ...props
}: DialogContentProps) {
  return (
    <DialogPortal>
      <DialogPrimitive.Backdrop
        data-slot="dialog-overlay"
        className={cn(
          "fixed inset-0 isolate z-(--layer-modal) bg-surface-scrim supports-backdrop-filter:backdrop-blur-xs",
          // The scrim takes the PANEL's duration and has none of its own. Give it one
          // and the two drift: the sheet in this repo dims at 150ms under a panel that
          // arrives at 200ms, so the page is dark before anything is there to read.
          instant ? "transition-none" : overlayScrim,
        )}
      />
      <DialogPrimitive.Popup
        data-slot="dialog-content"
        className={cn(
          "fixed top-1/2 left-1/2 z-(--layer-modal) grid w-full max-w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2",
          "gap-4 rounded-xl bg-popover p-4 text-shell-body text-popover-foreground ring-1 ring-surface-ring-quiet",
          "outline-none sm:max-w-sm",
          // Centre origin, unlike a popover. A dialog is not spatially owned by
          // whatever opened it, and a keystroke has no position on screen at all.
          instant ? "transition-none" : overlayMotion,
          className,
        )}
        {...props}
      >
        {children}
        {showCloseButton ? (
          <DialogPrimitive.Close
            data-slot="dialog-close"
            render={<Button variant="ghost" className="absolute top-2 right-2" size="icon-sm" />}
          >
            <XIcon />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        ) : null}
      </DialogPrimitive.Popup>
    </DialogPortal>
  );
}
